import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'

export interface PaginationState {
  page: number
  pageSize: number
  total: number
}

interface PaginationControlsProps {
  pagination: PaginationState
  pageCount: number
  paginationRange: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions?: number[]
  itemLabel?: string // e.g., "événement(s)", "organisateur(s)"
}

/**
 * Reusable Pagination Controls Component
 * Dark mode geek aesthetic
 */
export default function PaginationControls({
  pagination,
  pageCount,
  paginationRange,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  itemLabel = 'élément(s)',
}: PaginationControlsProps) {
  if (pageCount <= 1) return null

  return (
    <div className="flex items-center justify-between">
      <div className="text-xs text-slate-400 font-mono">
        {pagination.total} {itemLabel}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          disabled={pagination.page === 1}
          className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="mr-1" />
          Précédent
        </button>

        {paginationRange.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-2 rounded text-xs font-mono transition ${
              page === pagination.page
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(Math.min(pageCount, pagination.page + 1))}
          disabled={pagination.page === pageCount}
          className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Suivant
          <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
        </button>
      </div>

      <select
        value={pagination.pageSize}
        onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
        className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono focus:outline-none focus:border-cyan-500 transition"
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>
            {size} / page
          </option>
        ))}
      </select>
    </div>
  )
}
